import * as THREE from "../../libs/three.js/build/three.module.js";
import { ClipVolume } from "./ClipVolume.js";
import { PolygonClipVolume } from "./PolygonClipVolume.js";
import { EventDispatcher } from "../EventDispatcher.js";
import { MOUSE } from "../defines.js";

export class ClippingTool extends EventDispatcher {
  constructor(viewer) {
    super();

    this.viewer = viewer;

    this.maxPolygonVertices = 32;

    this.addEventListener("start_inserting_clipping_volume", (e) => {
      this.viewer.dispatchEvent({
        type: "cancel_insertions",
      });
    });

    this.sceneMarker = new THREE.Scene();
    this.sceneVolume = new THREE.Scene();
    this.sceneVolume.name = "scene_clip_volume";
    this.viewer.inputHandler.registerInteractiveScene(this.sceneVolume);

    this.onRemove = (e) => {
      this.sceneVolume.remove(e.volume);
    };

    this.onAdd = (e) => {
      this.sceneVolume.add(e.volume);
    };

    this.viewer.inputHandler.addEventListener("delete", (e) => {
      let volumes = e.selection.filter((e) => e instanceof ClipVolume);
      volumes.forEach((e) => this.viewer.scene.removeClipVolume(e));
      let polyVolumes = e.selection.filter(
        (e) => e instanceof PolygonClipVolume
      );
      polyVolumes.forEach((e) => this.viewer.scene.removePolygonClipVolume(e));
    });
  }

  setScene(scene) {
    if (this.scene === scene) {
      return;
    }

    if (this.scene) {
      this.scene.removeEventListeners("clip_volume_added", this.onAdd);
      this.scene.removeEventListeners("clip_volume_removed", this.onRemove);
      this.scene.removeEventListeners("polygon_clip_volume_added", this.onAdd);
      this.scene.removeEventListeners(
        "polygon_clip_volume_removed",
        this.onRemove
      );
    }

    this.scene = scene;

    this.scene.addEventListener("clip_volume_added", this.onAdd);
    this.scene.addEventListener("clip_volume_removed", this.onRemove);
    this.scene.addEventListener("polygon_clip_volume_added", this.onAdd);
    this.scene.addEventListener("polygon_clip_volume_removed", this.onRemove);
  }

  startInsertion(args = {}) {
    let type = args.type || null;

    if (!type) return null;

    let domElement = this.viewer.renderer.domElement;
    let canvasSize = this.viewer.renderer.getSize(new THREE.Vector2());

    let svg = $(`
<svg height="${canvasSize.height}" width="${canvasSize.width}" style="position:absolute; pointer-events: none">
  <defs>
    <marker id="diamond" markerWidth="12" markerHeight="12" refX="6" refY="6" markerUnits="userSpaceOnUse">
      <circle cx="6" cy="6" r="3" fill="#ff0000" />
    </marker>
  </defs>

  <polyline fill="none" stroke="#ff0000" stroke-width="2" 
    marker-start="url(#diamond)" 
    marker-mid="url(#diamond)" 
    marker-end="url(#diamond)" 
  />
</svg>
`);

    $(domElement.parentElement).append(svg);

    let polyClipVol = new PolygonClipVolume(
      this.viewer.scene.getActiveCamera().clone()
    );

    this.dispatchEvent({ type: "start_inserting_clipping_volume" });

    this.viewer.scene.addPolygonClipVolume(polyClipVol);
    this.sceneMarker.add(polyClipVol);

    let cancel = {
      callback: null,
    };

    let insertionCallback = (e) => {
      if (e.button === THREE.MOUSE.LEFT) {
        polyClipVol.addMarker();

        // SVC Screen Line
        svg.find("polyline").each((index, target) => {
          let newPoint = svg[0].createSVGPoint();
          newPoint.x = e.offsetX;
          newPoint.y = e.offsetY;
          let polyline = target.points.appendItem(newPoint);
        });

        // if (polyClipVol.markers.length > this.maxPolygonVertices) {
        //   cancel.callback();
        // }

        this.viewer.inputHandler.startDragging(
          polyClipVol.markers[polyClipVol.markers.length - 1]
        );
      } else if (e.button === THREE.MOUSE.RIGHT) {
        cancel.callback(e);
      }
    };

    cancel.callback = (e) => {
      let first = svg.find("polyline")[0].points[0];
      svg.find("polyline").each((index, target) => {
        let newPoint = svg[0].createSVGPoint();
        newPoint.x = first.x;
        newPoint.y = first.y;
        let polyline = target.points.appendItem(newPoint);
      });
      svg.remove();

      if (polyClipVol.markers.length > 3) {
        polyClipVol.removeLastMarker();
        polyClipVol.initialized = true;
      } else {
        this.viewer.scene.removePolygonClipVolume(polyClipVol);
      }

      this.viewer.renderer.domElement.removeEventListener(
        "mouseup",
        insertionCallback,
        false
      );
      this.viewer.removeEventListener("cancel_insertions", cancel.callback);
      this.viewer.inputHandler.enabled = true;
    };

    this.viewer.addEventListener("cancel_insertions", cancel.callback);
    this.viewer.renderer.domElement.addEventListener(
      "mouseup",
      insertionCallback,
      false
    );
    this.viewer.inputHandler.enabled = false;

    polyClipVol.addMarker();
    this.viewer.inputHandler.startDragging(
      polyClipVol.markers[polyClipVol.markers.length - 1]
    );
    return polyClipVol;
  }
  createPolygonClipVolumeFromPoints(points = [], measurementId) {
    if (!points || points.length < 3) {
      console.warn(
        "At least 3 points are required to create a PolygonClipVolume."
      );
      return null;
    }

    if (points.length > this.maxPolygonVertices) {
      console.warn(
        `Maximum number of vertices (${this.maxPolygonVertices}) exceeded. Only the first ${this.maxPolygonVertices} points will be used.`
      );
      points = points.slice(0, this.maxPolygonVertices);
    }

    let camera = this.viewer.scene.getActiveCamera().clone();
    let polyClipVol = new PolygonClipVolume(camera, measurementId);

    this.dispatchEvent({ type: "start_inserting_clipping_volume" });
    this.viewer.scene.addPolygonClipVolume(polyClipVol);
    this.sceneMarker.add(polyClipVol);

    // Convert world points to NDC space
    for (let point of points) {
      // Convert world point to camera space
      let cameraSpacePoint = point
        .clone()
        .applyMatrix4(camera.matrixWorldInverse);
      // Project to NDC space
      let ndcPoint = cameraSpacePoint.applyMatrix4(camera.projectionMatrix);
      // Create marker at NDC position
      let marker = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 16, 16),
        new THREE.MeshBasicMaterial({ color: "#00FF00" })
      );
      marker.position.copy(ndcPoint);
      polyClipVol.markers.push(marker);
    }

    polyClipVol.material = new THREE.MeshBasicMaterial({
      color: "#00FF00",
      side: THREE.DoubleSide,
    });

    this.viewer.setClipTask(Potree.ClipTask.HIGHLIGHT);
    this.viewer.setClipMethod(Potree.ClipMethod.INSIDE_ANY);
    polyClipVol.initialized = true;

    return polyClipVol;
  }

  createPolygonClipInside(points = [], measurementId) {
    if (!points || points.length < 3) {
      console.warn(
        "At least 3 points are required to create a PolygonClipVolume."
      );
      return null;
    }

    if (points.length > this.maxPolygonVertices) {
      console.warn(
        `Max ${this.maxPolygonVertices} vertices allowed. Truncating.`
      );
      points = points.slice(0, this.maxPolygonVertices);
    }

    let camera = this.viewer.scene.getActiveCamera().clone();
    let polyClipVol = new PolygonClipVolume(camera, measurementId);

    this.dispatchEvent({ type: "start_inserting_clipping_volume" });
    this.viewer.scene.addPolygonClipVolume(polyClipVol);
    this.sceneMarker.add(polyClipVol);

    for (let point of points) {
      let cameraSpacePoint = point
        .clone()
        .applyMatrix4(camera.matrixWorldInverse);
      let ndcPoint = cameraSpacePoint.applyMatrix4(camera.projectionMatrix);
      let marker = new THREE.Mesh(
        new THREE.SphereGeometry(0.1, 16, 16),
        new THREE.MeshBasicMaterial({ color: "#00FF00" })
      );
      marker.position.copy(ndcPoint);
      polyClipVol.markers.push(marker);
    }

    polyClipVol.initialized = true;

    this.viewer.setClipTask(Potree.ClipTask.SHOW_OUTSIDE);
    this.viewer.setClipMethod(Potree.ClipMethod.INSIDE_ANY);

    return polyClipVol;
  }

  update() {}
}
